import React from "react";
import { IContractPriceStep } from "../../utils/instruments";
import {
  IMarketInstrumentContextProviderProps,
  IOptionsChainContext,
} from "./types";
import { useActiveMarketData } from "./useActiveMarketData";
import { useOptionSelection } from "./useOptionSelection";
import { useParamSync } from "./useParamSync";

export type { IMarketPrecisionGetter } from "./types";
export { SELECTED_EXPIRY_OVERRIDE } from "./constants";

// Stores all the available markets,
// and the filters available
export const MarketInstrumentContext =
  React.createContext<IOptionsChainContext>({
    selectedOptionInstrument: undefined,
    selectedPerpetualInstrument: undefined,
    selectedSpotInstrument: undefined,
    selectedValues: {
      orderType: undefined,
      strike: undefined,
      expiry: undefined,
    },
    activeOptionMarkets: undefined,
    activePerpMarkets: undefined,
    activeSpotMarkets: undefined,
    marketsData: undefined,
    orderTypes: [],
    strikes: [],
    expiries: [],

    // OPTIONS
    setStrike: () => {},
    setExpiry: () => {},
    setOrderType: () => {},

    reset: () => {},
    getMarketPrecision: () => ({} as IContractPriceStep),
  });

export function MarketInstrumentContextProvider({
  children,
  params,
  onAssetDerivativeChanged,
}: IMarketInstrumentContextProviderProps) {
  const {
    market,
    setMarket,
    activeOptionMarkets,
    activePerpMarkets,
    activeSpotMarkets,
    fallbackMarket,
    marketsData,
    expiries,
    getMarketPrecision,
  } = useActiveMarketData();

  const {
    setStrike,
    setExpiry,
    setOrderType,
    orderTypes,
    strikes,
    selectedValues,
    selectedOptionInstrument,
    selectedPerpetualInstrument,
    selectedSpotInstrument,
    reset,
  } = useOptionSelection(market, marketsData, expiries);

  useParamSync({
    params,
    onAssetDerivativeChanged,
    market,
    setMarket,
    activeOptionMarkets,
    activePerpMarkets,
    activeSpotMarkets,
    fallbackMarket,
    marketsData,
    setStrike,
    setExpiry,
    setOrderType,
    selectedOptionInstrument,
    selectedPerpetualInstrument,
    selectedSpotInstrument,
  });

  return (
    <MarketInstrumentContext.Provider
      value={{
        marketsData,
        activeOptionMarkets,
        activePerpMarkets,
        activeSpotMarkets,
        selectedValues,
        selectedOptionInstrument,
        selectedPerpetualInstrument,
        selectedSpotInstrument,
        orderTypes,
        strikes,
        expiries,
        setStrike,
        setExpiry,
        setOrderType,
        reset,
        getMarketPrecision,
      }}
    >
      {children}
    </MarketInstrumentContext.Provider>
  );
}
